import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appName", "signupButton", "copyrightYears", "useDatabase", "caption", "hideFooter", "handleSuccess", "afterLogin", "url"];
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
// Derived from code from the Material UI project under the following license:
//
// The MIT License (MIT)
//
// Copyright (c) 2014 Call-Em-All
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
//
import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import { useHistory, useLocation } from "react-router-dom";
import { FormHelperText } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Copyright from "../Copyright";
import AsyncComboBox from "../base/AsyncComboBox";
import * as Utils from "../Utils";
import VersionInfo from "../VersionInfo";
var useStyles = makeStyles(function (theme) {
  return {
    topSpacer: {
      marginTop: theme.spacing(15)
    },
    paper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.secondary.main
    },
    form: {
      width: "100%",
      // Fix IE 11 issue.
      marginTop: theme.spacing(1)
    },
    submit: {
      margin: theme.spacing(2, 0, 1)
    },
    link: {
      cursor: "pointer",
      fontWeight: 'bold'
    }
  };
});
export default function Login(_ref) {
  var appName = _ref.appName,
    signupButton = _ref.signupButton,
    copyrightYears = _ref.copyrightYears,
    useDatabase = _ref.useDatabase,
    caption = _ref.caption,
    hideFooter = _ref.hideFooter,
    handleSuccess = _ref.handleSuccess,
    afterLogin = _ref.afterLogin,
    url = _ref.url,
    props = _objectWithoutProperties(_ref, _excluded);
  var classes = useStyles();
  var _useState = useState(""),
    _useState2 = _slicedToArray(_useState, 2),
    username = _useState2[0],
    setUsername = _useState2[1];
  var _useState3 = useState(""),
    _useState4 = _slicedToArray(_useState3, 2),
    password = _useState4[0],
    setPassword = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    errorMessage = _useState6[0],
    setErrorMessage = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isLoginRequestPending = _useState8[0],
    setLoginRequestPending = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isTokenUploadPending = _useState10[0],
    setTokenUploadPending = _useState10[1];
  var _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    databaseOption = _useState12[0],
    setDatabaseOption = _useState12[1];
  var _useState13 = useState({
      showDatabaseDropDownList: false,
      usernameFieldEnabled: true,
      usernameValue: '',
      showPasswordField: true,
      loginButtonEnabled: true,
      hasSamlToken: false,
      samlLogin: false,
      saasSamlLogin: false,
      downloadSamlTokenUrl: null,
      tokenIssuedTimestamp: null
    }),
    _useState14 = _slicedToArray(_useState13, 2),
    loginSettings = _useState14[0],
    setLoginSettings = _useState14[1];
  var history = useHistory();
  var location = useLocation();

  /**
   * Downloads the login settings from the server.
   */
  React.useEffect(function () {
    var retrieveLoginSettingsUrl;
    if (useDatabase) {
      retrieveLoginSettingsUrl = "cloudapi/configuration/retrieveLoginSettings";
    } else {
      retrieveLoginSettingsUrl = "application/auth/retrieveLoginSettings";
    }
    Utils.fetchData(retrieveLoginSettingsUrl, null, false, true).then(function (response) {
      console.table(response);
      setLoginSettings(response);
      processSettingsUpdated(response);
    }).catch(function (response) {
      Utils.getErrorMessage(response, "Retrieve settings failed.").then(setErrorMessage);
    });
  }, []);

  /**
   * **********************************
   * Handles the token operations
   */

  var processSettingsUpdated = function processSettingsUpdated(loginSettings) {
    //localStorage.setItem("saml-assertion-token:token", tokenContents.token);
    //localStorage.setItem("saml-assertion-token:user", tokenContents.username);
    if (loginSettings.samlLogin) {
      var token = localStorage.getItem("saml-assertion-token:token");
      if (token) {
        console.log("There is a token saved in the local storage");
        console.log("Token contents: " + token);
        uploadTokenContentsToServer(token, "cloudapi/configuration/uploadSamlAssertionContents");
      }
    }
    if (loginSettings.usernameValue) {
      setUsername(loginSettings.usernameValue);
    }
  };

  /**
   * Shows a popup window where the user can download the SAML assertion token from.
   */
  var getToken = function getToken() {
    window.open(loginSettings.downloadSamlTokenUrl);
  };

  /**
   * Shows the dialog to upload the token file to the server.
   */
  var uploadSamlAssertionTokenFile = function uploadSamlAssertionTokenFile() {
    var fileSelect = document.getElementById("contained-button-file");
    fileSelect.click();
  };

  /**
   * Executed when the user actually picks a SAML assertion token file. This is what uploads it
   *  to the server.
   */
  var uploadSamlAssertionTokenFileAfterSelection = function uploadSamlAssertionTokenFileAfterSelection(e) {
    var fileSelect = document.getElementById("contained-button-file");
    var selectedFile = fileSelect.files[0];

    // The token can only be uploaded to the Cloud API URL
    uploadTokenToServer(selectedFile, "cloudapi/configuration/uploadSamlAssertion");
  };

  /**
   * Upload the SAML token to the server. The response will be the username, the token, and possibly some extra information,
   *  or an error message in case something goes wrong.
   */
  var uploadTokenToServer = function uploadTokenToServer(file, uri) {
    console.log("Submitting file [" + file + "] to URL [" + uri + "]");
    setTokenUploadPending(true);
    var data = new FormData();
    data.append("file", file);
    Utils.fetchData(uri, {
      method: "POST",
      body: data
    }, false, true).then(function (tokenContents) {
      setTokenUploadPending(false);
      console.log("Response received from server");
      console.table(tokenContents);
      localStorage.setItem("saml-assertion-token:token", tokenContents.token);
      localStorage.setItem("saml-assertion-token:user", tokenContents.username);

      //
      // Process the response contents
      processTokenResponse(tokenContents);
    }).catch(handleUploadTokenError);
  };
  var uploadTokenContentsToServer = function uploadTokenContentsToServer(contents, uri) {
    console.log("Submitting token contents to URL [" + uri + "]");
    setTokenUploadPending(true);
    Utils.fetchData(uri, {
      method: "POST",
      body: JSON.stringify({
        token: contents
      })
    }, true, true).then(function (tokenContents) {
      setTokenUploadPending(false);

      //
      // Process the response contents
      processTokenResponse(tokenContents);
    }).catch(handleUploadTokenError);
  };

  /**
   * Process a token response received from the server after a token has been received
   */
  var processTokenResponse = function processTokenResponse(tokenContents) {
    // Sets the new login state with the properties received from the server
    setUsername(tokenContents.username);
    setLoginSettings(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        usernameValue: tokenContents.username,
        loginButtonEnabled: true,
        hasSamlToken: true
      });
    });
  };

  /**
   * Invoked when the token upload fails for whichever reason.
   */
  var handleUploadTokenError = function handleUploadTokenError(response) {
    setTokenUploadPending(false);
    Utils.getErrorMessage(response, "Token upload failed.").then(setErrorMessage);
  };

  /**
   * Removes the SAML token and reloads the page.
   */
  var handleRemoveSamlToken = function handleRemoveSamlToken() {
    localStorage.removeItem("saml-assertion-token:token");
    localStorage.removeItem("saml-assertion-token:user");
    window.location.reload();
  };

  /**
   * **********************************
   * Handles the login operation
   */

  var login = function login() {
    issueUserLoginRequest();
  };
  var _ref2 = location.state || {
      from: {
        pathname: "/"
      }
    },
    from = _ref2.from;
  var issueUserLoginRequest = function issueUserLoginRequest() {
    setErrorMessage(null);
    setLoginRequestPending(true);
    var loginUrl = url;
    if (!loginUrl) {
      if (useDatabase) {
        loginUrl = "cloudapi/configuration/login";
      } else {
        loginUrl = "application/auth/login";
      }
    }
    var details = {
      username: username,
      // TODO use base64 encoding
      password: password
    };
    if (useDatabase) {
      details.db = JSON.stringify(databaseOption);
    }
    var formBody = [];
    for (var property in details) {
      var encodedKey = encodeURIComponent(property);
      var encodedValue = encodeURIComponent(details[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    formBody = formBody.join("&");
    return Utils.fetchData(loginUrl, {
      method: "post",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: formBody
    }, false, false).catch(handleLoginErrorResponse).then(handleLoginSuccessResponse);
  };
  var handleLoginSuccessResponse = function handleLoginSuccessResponse(response) {
    setLoginRequestPending(false);
    if (response) {
      if (response.ok) {
        if (handleSuccess) {
          handleSuccess(response);
        } else {
          history.replace(from);
        }
        if (afterLogin) {
          afterLogin();
        }
      } else {
        setErrorMessage("Authentication failed");
      }
    }
  };
  var handleLoginErrorResponse = function handleLoginErrorResponse(response) {
    setLoginRequestPending(false);
    Utils.getErrorMessage(response, "Authentication failed.").then(setErrorMessage);
  };

  /* ********************************** */

  var onChangeUsername = function onChangeUsername(event) {
    setUsername(event.target.value);
  };
  var onChangePassword = function onChangePassword(event) {
    setPassword(event.target.value);
  };
  var onKeyPressed = function onKeyPressed(event) {
    if (event.key === "Enter") {
      login();
    }
  };
  return /*#__PURE__*/React.createElement(Container, Object.assign({
    component: "main",
    className: classes.topSpacer,
    maxWidth: "xs"
  }, props), /*#__PURE__*/React.createElement(CssBaseline, null), /*#__PURE__*/React.createElement("div", {
    className: classes.paper,
    onKeyPress: onKeyPressed
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, /*#__PURE__*/React.createElement("img", {
    src: "static/images/logo.png",
    title: "Logo",
    alt: "Logo",
    height: "50px"
  })), /*#__PURE__*/React.createElement(Box, {
    flexShrink: 1,
    m: 1
  }, /*#__PURE__*/React.createElement("img", {
    src: "static/images/verticalSquares.gif",
    title: "Logo",
    alt: "Logo",
    height: "50px"
  }))), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 0
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, caption), useDatabase && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(AsyncComboBox, {
    key: "dbs-input",
    url: "cloudapi/configuration/dbs",
    autoOpen: true,
    disabled: isLoginRequestPending,
    textFieldProps: {
      fullWidth: true,
      label: "Database"
    },
    onChange: function onChange(event, value) {
      return setDatabaseOption(value);
    },
    value: databaseOption,
    style: {
      width: "100%"
    },
    getOptionLabel: function getOptionLabel(option) {
      return option.caption;
    },
    autoSelect: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    variant: "outlined",
    margin: "normal",
    value: username,
    disabled: !loginSettings.usernameFieldEnabled,
    required: true,
    fullWidth: true,
    id: "username",
    label: "Username",
    name: "username",
    autoComplete: "username",
    onChange: onChangeUsername,
    autoFocus: true
  })),
  /**
   * Password field - it is only shown if the login is configured to let it be shown.
   */
  loginSettings.showPasswordField && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    variant: "outlined",
    margin: "normal",
    required: true,
    fullWidth: true,
    name: "password",
    label: "Password",
    type: "password",
    id: "password",
    onChange: onChangePassword,
    autoComplete: "current-password"
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormHelperText, {
    error: true
  }, errorMessage)), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    fullWidth: true,
    variant: "contained",
    color: "primary",
    className: classes.submit,
    disabled: !databaseOption && useDatabase || isLoginRequestPending || !loginSettings.loginButtonEnabled,
    onClick: login
  }, "Sign In", isLoginRequestPending && /*#__PURE__*/React.createElement(CircularProgress, {
    color: "inherit",
    style: {
      marginLeft: "10px"
    },
    size: 20
  }))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, signupButton),
  /**
   * These are the controls to download the token from the WS URL and to upload
   *  the token to P6.
   */
  loginSettings.samlLogin && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 1,
    xs: 12
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    noWrap: true
  }, "This application is using SSO/ SAML authentication."))), loginSettings.hasSamlToken && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, "A SAML token was found and can be used to login to the server - just click on the [Sign In] button. To remove this saved token, click ", /*#__PURE__*/React.createElement(Link, {
    className: classes.link,
    color: "inherit",
    onClick: handleRemoveSamlToken
  }, "here"), ". To download a new token, click the [Get Token] button, and then upload it with the [Upload Token] button."))), !loginSettings.hasSamlToken && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, "No SAML token was found - to retrieve a new token from the server click the [Get Token] button. After saving the token, upload it using the [Upload Token] button."))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "contained",
    color: "primary",
    className: classes.submit,
    fullWidth: true,
    onClick: getToken
  }, "Get Token")), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement("input", {
    type: "file",
    accept: "text/xml",
    style: {
      display: 'none'
    },
    onChange: uploadSamlAssertionTokenFileAfterSelection,
    id: "contained-button-file"
  }), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "contained",
    color: "primary",
    className: classes.submit,
    fullWidth: true,
    onClick: uploadSamlAssertionTokenFile,
    disabled: isTokenUploadPending
  }, "Upload Token", isTokenUploadPending && /*#__PURE__*/React.createElement(CircularProgress, {
    color: "inherit",
    style: {
      marginLeft: "10px"
    },
    size: 20
  })))))), !hideFooter && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Box, {
    mt: 2
  }, /*#__PURE__*/React.createElement("hr", null), appName && /*#__PURE__*/React.createElement(VersionInfo, {
    appName: appName
  })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Copyright, {
    years: copyrightYears
  }))));
}